import { X } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import {
    isAnnouncementExternalHref,
    normalizeAnnouncement,
} from '@/lib/announcementBanner.mjs';
import { cn } from '@/lib/utils';

const BANNER_TRANSITION_MS = 450;

function AnnouncementCta({ cta, className, onClick }) {
    if (!cta?.href || !cta?.label) {
        return null;
    }

    const sharedProps = {
        className: `${className} inline-flex shrink-0 translate-y-px items-center justify-center whitespace-nowrap border-b border-white/60 text-sm font-semibold leading-[1.15] transition-colors hover:border-white`,
        onClick,
    };

    if (isAnnouncementExternalHref(cta.href)) {
        return (
            <a
                href={cta.href}
                rel="noreferrer"
                target="_blank"
                {...sharedProps}
            >
                {cta.label}
            </a>
        );
    }

    return (
        <Link href={cta.href} {...sharedProps}>
            {cta.label}
        </Link>
    );
}

export default function SiteAnnouncementBanner({
    announcement,
    isFloating = false,
    onHeightChange,
    onDismiss,
    isVisible,
}) {
    const normalizedAnnouncement = normalizeAnnouncement(announcement);
    const [renderedVisible, setRenderedVisible] = useState(false);
    const [measuredHeight, setMeasuredHeight] = useState(0);
    const dismissTimeoutRef = useRef(null);
    const sectionRef = useRef(null);
    const isDismissible = typeof onDismiss === 'function';
    const isVisibilityControlled = isVisible !== undefined;

    useEffect(() => {
        if (dismissTimeoutRef.current) {
            clearTimeout(dismissTimeoutRef.current);
            dismissTimeoutRef.current = null;
        }

        if (isVisibilityControlled) {
            setRenderedVisible(Boolean(isVisible));
            return;
        }

        const frame = requestAnimationFrame(() => {
            setRenderedVisible(true);
        });

        return () => {
            cancelAnimationFrame(frame);
        };
    }, [isVisibilityControlled, isVisible]);

    useEffect(
        () => () => {
            if (dismissTimeoutRef.current) {
                clearTimeout(dismissTimeoutRef.current);
            }

            onHeightChange?.(0);
        },
        [onHeightChange],
    );

    useEffect(() => {
        if (!normalizedAnnouncement || !onHeightChange) {
            return;
        }

        const section = sectionRef.current;
        if (!section) {
            return;
        }

        const updateHeight = () => {
            const nextHeight = section.getBoundingClientRect().height;

            setMeasuredHeight((current) =>
                current === nextHeight ? current : nextHeight,
            );
        };

        updateHeight();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(() => {
            updateHeight();
        });

        observer.observe(section);

        return () => {
            observer.disconnect();
        };
    }, [normalizedAnnouncement, onHeightChange]);

    useEffect(() => {
        if (!onHeightChange) {
            return;
        }

        if (!normalizedAnnouncement || !renderedVisible) {
            onHeightChange(0);
            return;
        }

        onHeightChange(measuredHeight);
    }, [
        measuredHeight,
        normalizedAnnouncement,
        onHeightChange,
        renderedVisible,
    ]);

    const handleDismiss = ({ immediate = false } = {}) => {
        if (!isDismissible) {
            return;
        }

        if (isVisibilityControlled) {
            onDismiss();
            return;
        }

        if (dismissTimeoutRef.current) {
            clearTimeout(dismissTimeoutRef.current);
            dismissTimeoutRef.current = null;
        }

        if (immediate) {
            setRenderedVisible(false);
            onHeightChange?.(0);
            onDismiss();
            return;
        }

        setRenderedVisible(false);
        dismissTimeoutRef.current = setTimeout(() => {
            onDismiss();
            dismissTimeoutRef.current = null;
        }, BANNER_TRANSITION_MS);
    };

    const handleCtaClick = () => {
        handleDismiss({ immediate: true });
    };

    if (!normalizedAnnouncement) {
        return null;
    }

    const badgeLabel = normalizedAnnouncement.badge;
    const content = normalizedAnnouncement.message;

    return (
        <div
            className={cn(
                'origin-top overflow-hidden will-change-[max-height,opacity,transform] transition-[max-height,opacity,transform] duration-450 ease-[cubic-bezier(0.22,1,0.36,1)]',
                isFloating
                    ? 'pointer-events-none fixed inset-x-0 top-0 z-60'
                    : '',
                renderedVisible
                    ? 'max-h-60 translate-y-0 opacity-100'
                    : 'pointer-events-none max-h-0 -translate-y-2 opacity-0',
            )}
        >
            <section
                ref={sectionRef}
                aria-label="Site announcement"
                aria-hidden={!renderedVisible}
                className={cn(
                    'border-b border-[#a83a00]/18 bg-[#ff6a00] text-white',
                    isFloating
                        ? 'pointer-events-auto shadow-[0_10px_30px_rgba(0,0,0,0.12)]'
                        : '',
                )}
                style={{
                    backgroundImage:
                        'radial-gradient(ellipse 150% 260% at 50% 50%, rgba(191,67,0,0.76) 0%, rgba(204,74,0,0.66) 24%, rgba(208,74,6,0.46) 44%, rgba(255,106,0,0.12) 70%), linear-gradient(90deg, #ff7a1a 0%, #ff6a00 48%, #f59a3d 100%)',
                }}
            >
                <div className="mx-auto w-full px-4 py-3 sm:px-6 max-w-7xl lg:px-8">
                    <div
                        className={cn(
                            'grid w-full items-center gap-x-3 text-sm sm:text-[15px]',
                            isDismissible
                                ? 'grid-cols-[2rem_minmax(0,1fr)_2rem]'
                                : 'grid-cols-1',
                        )}
                    >
                        {isDismissible ? (
                            <div aria-hidden className="h-8 w-8" />
                        ) : null}

                        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center">
                            {badgeLabel ? (
                                <span className="hidden items-center rounded-full border border-white/20 bg-white/12 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/95 sm:inline-flex">
                                    {badgeLabel}
                                </span>
                            ) : null}

                            {content ? (
                                <p className="max-w-4xl font-medium leading-[1.35] text-white">
                                    {content}
                                </p>
                            ) : null}

                            <AnnouncementCta
                                cta={normalizedAnnouncement.cta}
                                className="text-white"
                                onClick={
                                    isDismissible ? handleCtaClick : undefined
                                }
                            />
                        </div>

                        {isDismissible ? (
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleDismiss}
                                    className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-white/92 transition-colors hover:bg-white/14 hover:text-white"
                                    aria-label="Dismiss announcement"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </section>
        </div>
    );
}
